/**
 * Token visual system (ART-BIBLE §4).
 *
 * Hundreds of tokens, four material families, twelve tags that must COMPOSE.
 * The composition trick: three disjoint channels.
 *   RIM  (coin edge)  Magnet · Gummi · Schwer · Zins        → in-shader
 *   FACE (coin face)  Spalt · Gold · Fluch · Gluecks        → in-shader
 *   AURA (outside)    Geist · Kleber · Feuer · Echo         → sprite pools
 * A second tag on the same channel degrades to a pip at 4 o'clock, so a token never
 * loses information — it just says the loud part loudly.
 *
 * Draw calls: 4 bodies + 4 ink hulls + 1 magnet-arc line batch = 9. The AURA pools are
 * shared with the rest of the kit (2 more).
 */

import {
  Group, InstancedMesh, CylinderGeometry, InstancedBufferAttribute, Matrix4, Quaternion,
  Vector3, Color, LineSegments, BufferGeometry, BufferAttribute, LineBasicMaterial,
  AdditiveBlending, type Camera, type Scene,
} from 'three';
import type { Material as MaterialName, Tag, TokenState } from '../../../shared/contract';
import { MATERIALS, TAG_COLOR, mixHex } from '../art/palette';
import { toon, TAG_BITS } from '../art/toon';
import { inkOutlineInstanced, computeHullNormals } from '../art/outline';
import { SpriteField } from '../fx/sprites';

export const MATERIAL_ORDER: MaterialName[] = ['kupfer', 'silber', 'gold', 'obsidian'];

/** contract Tag union order == priority order (earlier wins its channel) */
export const TAG_ORDER: Tag[] = [
  'Magnet', 'Geist', 'Gummi', 'Kleber', 'Spalt', 'Gold',
  'Fluch', 'Feuer', 'Gluecks', 'Schwer', 'Zins', 'Echo',
];

/** the tag a combo is *named* by: the loudest one, i.e. first in the contract's order */
export function loudestTag(tags: Tag[]): Tag | null {
  let best: Tag | null = null;
  let bestI = Infinity;
  for (const t of tags) {
    const i = TAG_ORDER.indexOf(t);
    if (i >= 0 && i < bestI) { bestI = i; best = t; }
  }
  return best;
}

const CHANNEL: Record<Tag, 'RIM' | 'FACE' | 'AURA'> = {
  Magnet: 'RIM', Gummi: 'RIM', Schwer: 'RIM', Zins: 'RIM',
  Spalt: 'FACE', Gold: 'FACE', Fluch: 'FACE', Gluecks: 'FACE',
  Geist: 'AURA', Kleber: 'AURA', Feuer: 'AURA', Echo: 'AURA',
};

export interface TokenView {
  id: number;
  material: MaterialName;
  radius: number;
  tags: Tag[];
  pos: [number, number, number];
  quat: [number, number, number, number];
  age?: number;
  /** 0..1 squash amount from the last impact (Gummi reads this hardest) */
  squash?: number;
  faceValue?: number;
  /**
   * **contract v1** — welded into the field by [Kleber]: this coin is terrain now.
   * Distinct from `asleep`, which is also true of the ~226 ordinary pile coins. A stuck
   * coin is drawn as tarred rather than merely still: dulled body, no impact squash,
   * a drip that has already run and set.
   */
  stuck?: boolean;
}

const _m = new Matrix4();
const _q = new Quaternion();
const _p = new Vector3();
const _s = new Vector3();
const _c = new Color();

/**
 * **The contract-v1 rebase — the one place the renderer moves tokens out of world space.**
 *
 * `TokenState.pos` is a WORLD position in the sim's single Rapier world, where cabinets sit
 * at distinct `CabinetState.origin`s. That is what makes *Der Deckel* — a token physically
 * rolling out of one cabinet into its neighbour — honest instead of scripted: a token in
 * flight between two machines has no cabinet frame to be expressed in.
 *
 * The pool is nevertheless parented to a cabinet's `Object3D`, because that is what keeps a
 * pusher's hoard in shape when Der Croupier wheels the machine down a floor. So every
 * position is drawn at `P − O`: one vector subtraction per token, and when a cabinet
 * travels only `O` changes.
 *
 * Pass the wrong origin (or none) and the Münzschieber's pile is drawn sixteen world units
 * to the right of its own pusher, sitting inside the Bandit.
 */
export function rebase(tokens: TokenState[], origin: [number, number, number] | Vector3): TokenView[] {
  const ox = Array.isArray(origin) ? origin[0] : origin.x;
  const oy = Array.isArray(origin) ? origin[1] : origin.y;
  const oz = Array.isArray(origin) ? origin[2] : origin.z;
  return tokens.map((t) => ({
    id: t.id, material: t.material, radius: t.radius, tags: t.tags,
    pos: [t.pos[0] - ox, t.pos[1] - oy, t.pos[2] - oz] as [number, number, number],
    quat: t.quat, age: t.age, faceValue: t.faceValue, stuck: t.stuck,
  }));
}

/** contract note: token local +Z is the coin's face normal (see CONTRACT-CHANGES.md) */
function coinGeometry(): CylinderGeometry {
  const g = new CylinderGeometry(0.5, 0.5, 0.17, 22, 1, false);
  g.rotateX(Math.PI / 2);
  computeHullNormals(g);
  return g;
}

interface Family {
  body: InstancedMesh;
  ink: InstancedMesh;
  aTint: InstancedBufferAttribute;
  aEmi: InstancedBufferAttribute;
  aPip: InstancedBufferAttribute;
  aTag: InstancedBufferAttribute;
  aPhase: InstancedBufferAttribute;
  aWidth: InstancedBufferAttribute;
  n: number;
  /** something was written into this family's buffers this frame */
  dirty: boolean;
}

/** what a packed instance already holds, so an unchanged one can be skipped entirely */
interface Packed { i: number; x: number; y: number; z: number }

export interface TokenSystemOpts {
  capacity?: number;
  sparks?: SpriteField;
  smoke?: SpriteField;
}

export class TokenSystem {
  group = new Group();
  families = new Map<MaterialName, Family>();
  private cap: number;
  private sparks?: SpriteField;
  private smoke?: SpriteField;
  private arcGeo: BufferGeometry;
  private arcPos: Float32Array;
  private arcCol: Float32Array;
  private arcs: LineSegments;
  private squash = new Map<number, { t: number; dir: Vector3 }>();
  private wisp = new Map<number, Vector3[]>();
  /** tokens the sim has turned into fixed bodies (contract v1 `sediment`) */
  private packed = new Map<number, Packed | null>();
  /**
   * Instances skipped by the packed fast path last frame. Reported by `kit.stats()`,
   * because ART-BIBLE §9's numbers are measured, not claimed.
   */
  skipped = 0;
  private time = 0;

  constructor(opts: TokenSystemOpts = {}) {
    this.cap = opts.capacity ?? 420;
    this.sparks = opts.sparks;
    this.smoke = opts.smoke;
    this.group.name = 'tokens';

    const geo = coinGeometry();
    for (const name of MATERIAL_ORDER) {
      const pal = MATERIALS[name];
      const mat = toon({
        color: 0xffffff,
        ramp: 'METAL4',
        rim: 0.55,
        halftone: 0,
        tokenFx: true,
        transparent: true,
        opacity: 1,
        name: `token:${name}`,
      });
      const body = new InstancedMesh(geo, mat, this.cap);
      body.frustumCulled = false;
      body.count = 0;
      body.castShadow = true;
      // tokens are 8 cm across: self-shadowing at this scale is pure acne, not shading
      body.receiveShadow = false;
      body.name = `tokens:${name}`;
      body.userData.noInk = true;

      const mk = (n: number, fill: number) =>
        new InstancedBufferAttribute(new Float32Array(this.cap * n).fill(fill), n);
      const aTint = mk(3, 1), aEmi = mk(3, 0), aPip = mk(3, 0), aTag = mk(1, 0), aPhase = mk(1, 0), aWidth = mk(1, 1);
      geo.setAttribute('aTint', aTint);   // NOTE: replaced per family below
      body.geometry = geo.clone();
      body.geometry.setAttribute('aTint', aTint);
      body.geometry.setAttribute('aEmi', aEmi);
      body.geometry.setAttribute('aPip', aPip);
      body.geometry.setAttribute('aTag', aTag);
      body.geometry.setAttribute('aPhase', aPhase);
      body.geometry.setAttribute('aWidth', aWidth);
      computeHullNormals(body.geometry);

      const ink = inkOutlineInstanced(body, { widthPx: 1.6, body: pal.base, instanced: true });
      ink.geometry = body.geometry;

      this.group.add(ink, body);
      this.families.set(name, { body, ink, aTint, aEmi, aPip, aTag, aPhase, aWidth, n: 0, dirty: true });
      void pal;
    }

    // magnet arcs: one LineSegments batch for every spark line in the scene
    const maxArcs = 96;
    this.arcPos = new Float32Array(maxArcs * 2 * 3);
    this.arcCol = new Float32Array(maxArcs * 2 * 3);
    this.arcGeo = new BufferGeometry();
    this.arcGeo.setAttribute('position', new BufferAttribute(this.arcPos, 3));
    this.arcGeo.setAttribute('color', new BufferAttribute(this.arcCol, 3));
    this.arcs = new LineSegments(this.arcGeo, new LineBasicMaterial({
      vertexColors: true, transparent: true, blending: AdditiveBlending,
      depthWrite: false, toneMapped: false,
    }));
    this.arcs.frustumCulled = false;
    this.arcs.userData.noInk = true;
    this.arcGeo.setDrawRange(0, 0);
    this.group.add(this.arcs);
  }

  addTo(scene: Scene | Group): this { scene.add(this.group); return this; }

  /** call on a `collision` event — drives [Gummi] squash and [Schwer] dust */
  impact(id: number, impulse: number, at?: [number, number, number] | Vector3, dir?: Vector3): void {
    const v = Math.min(1, impulse / 6);
    if (v < 0.05) return;
    if (this.packed.has(id)) return;      // fixed bodies do not bounce
    this.squash.set(id, { t: v, dir: (dir ?? new Vector3(0, 1, 0)).clone().normalize() });
    void at;
  }

  /**
   * **contract v1 `sediment`** — the sim buried this coin and turned it into a fixed body.
   *
   * This is a performance event, not a visual one: on a typical Münzschieber frame roughly
   * 200 of 300 instances are packed, and packed means their transform and their entire
   * per-instance look are provably identical to the frame before. Skipping those writes
   * (16 floats of matrix + 11 of attributes each) is the difference between uploading the
   * pile every frame and uploading only what moved. A packed coin must look EXACTLY as it
   * did — so the skip is guarded by the position it was last written at, and any
   * disagreement re-writes it rather than trusting the flag.
   */
  setPacked(id: number, packed: boolean): void {
    if (packed) { if (!this.packed.has(id)) this.packed.set(id, null); }
    else this.packed.delete(id);
  }

  /** forget every packed body — a new machine, a new pile */
  clearPacked(): void { this.packed.clear(); }

  /**
   * Resolve which tags get their full signature and which degrade to a pip.
   * Returns [bitmask, pipColor].
   */
  static compose(tags: Tag[]): [number, number] {
    const seen: Record<string, Tag | undefined> = {};
    let mask = 0;
    let pip = 0;
    const sorted = [...tags].sort((a, b) => TAG_ORDER.indexOf(a) - TAG_ORDER.indexOf(b));
    for (const t of sorted) {
      const ch = CHANNEL[t];
      if (!seen[ch]) {
        seen[ch] = t;
        mask |= 1 << TAG_BITS[t];
      } else if (!pip) {
        pip = TAG_COLOR[t];
      }
    }
    return [mask, pip];
  }

  /** one instance: transform + the four look attributes. The only place that writes them. */
  private writeInstance(fam: Family, i: number, t: TokenView, sq?: { t: number; dir: Vector3 }): void {
    const [mask, pip] = TokenSystem.compose(t.tags);
    const pal = MATERIALS[t.material];
    fam.dirty = true;

    // --- transform, with [Gummi] squash-and-stretch on impact ------------
    _p.set(t.pos[0], t.pos[1], t.pos[2]);
    _q.set(t.quat[0], t.quat[1], t.quat[2], t.quat[3]);
    const r = t.radius / 0.5;
    // a coin welded into the field never squashes: it is terrain, not a body
    if (sq && sq.t > 0.01 && !t.stuck) {
      const isGummi = t.tags.includes('Gummi');
      const amp = (isGummi ? 0.30 : 0.10) * sq.t;
      _s.set(r * (1 + amp), r * (1 + amp), r * (1 - amp * 1.6));
    } else {
      _s.set(r, r, r);
    }
    _m.compose(_p, _q, _s);
    fam.body.setMatrixAt(i, _m);

    // --- per-instance look ------------------------------------------------
    // [Kleber] terrain reads as tarred: the body dulls toward the sticky senf shade,
    // which is what separates a welded coin from the ~226 that are merely asleep.
    _c.set(t.stuck ? mixHex(pal.base, TAG_COLOR.Kleber, 0.16) : pal.base);
    if (t.stuck) _c.multiplyScalar(0.82);
    fam.aTint.setXYZ(i, _c.r, _c.g, _c.b);

    let emi = 0x000000;
    if (t.tags.includes('Gold')) emi = mixHex(emi, 0x2a1e04, 1);
    if (t.tags.includes('Feuer')) emi = mixHex(emi, 0x331004, 1);
    if (t.tags.includes('Fluch')) emi = mixHex(emi, 0x1c0630, 1);
    if (t.tags.includes('Zins')) {
      const grow = Math.min(1, ((t.age ?? 0) % 10) / 10);
      emi = mixHex(emi, 0x2a1a00, 0.4 + grow * 0.6);
    }
    _c.set(emi);
    fam.aEmi.setXYZ(i, _c.r, _c.g, _c.b);

    _c.set(pip);
    fam.aPip.setXYZ(i, pip ? _c.r : 0, pip ? _c.g : 0, pip ? _c.b : 0);

    fam.aTag.setX(i, mask);
    fam.aPhase.setX(i, ((t.id * 37) % 100) / 100);
    // [Schwer] is drawn heavier — weight is a line width, not just a mass
    fam.aWidth.setX(i, t.tags.includes('Schwer') ? 2.0 : 1.0);
  }

  /** write one frame of tokens into the instance buffers */
  sync(tokens: TokenView[]): void {
    for (const f of this.families.values()) { f.n = 0; f.dirty = false; }
    this.skipped = 0;
    let arcN = 0;
    const magnets: TokenView[] = [];

    for (const t of tokens) {
      const fam = this.families.get(t.material);
      if (!fam || fam.n >= this.cap) continue;
      const i = fam.n++;

      const sq = this.squash.get(t.id);

      // --- packed bodies: identical instance, already in the buffer -----------
      // A [Zins] coin's emissive is driven from `age` on the CPU, so it is never packable
      // however still it lies. Everything else about a packed coin is frozen by definition.
      let skip = false;
      if (this.packed.has(t.id) && !sq && !t.tags.includes('Zins')) {
        const p = this.packed.get(t.id);
        if (p && p.i === i && p.x === t.pos[0] && p.y === t.pos[1] && p.z === t.pos[2]) skip = true;
        else this.packed.set(t.id, { i, x: t.pos[0], y: t.pos[1], z: t.pos[2] });
      }

      if (skip) this.skipped++;
      else this.writeInstance(fam, i, t, sq);

      // --- AURA channel ------------------------------------------------------
      if (t.tags.includes('Geist')) this.geistWisps(t);
      if (t.tags.includes('Fluch')) this.fluchSmolder(t);
      if (t.tags.includes('Feuer')) this.feuerEmber(t);
      if (t.tags.includes('Kleber')) this.kleberDrip(t);
      if (t.tags.includes('Echo')) this.echoImage(t);
      if (t.tags.includes('Magnet')) magnets.push(t);
    }

    // --- [Magnet] arcs: to the nearest two magnets within 4u -----------------
    for (let a = 0; a < magnets.length && arcN < 96; a++) {
      let linked = 0;
      for (let b = 0; b < magnets.length && linked < 2 && arcN < 96; b++) {
        if (a === b) continue;
        const A = magnets[a].pos, B = magnets[b].pos;
        const d = Math.hypot(A[0] - B[0], A[1] - B[1], A[2] - B[2]);
        if (d > 4.2 || d < 0.01) continue;
        linked++;
        const jit = (k: number) => (Math.sin(this.time * 41 + a * 3.1 + b * 7.7 + k) * 0.12);
        const o = arcN * 6;
        this.arcPos[o] = A[0]; this.arcPos[o + 1] = A[1] + jit(0); this.arcPos[o + 2] = A[2];
        this.arcPos[o + 3] = B[0]; this.arcPos[o + 4] = B[1] + jit(1); this.arcPos[o + 5] = B[2];
        _c.set(TAG_COLOR.Magnet);
        const fade = 1 - d / 4.2;
        for (let k = 0; k < 2; k++) {
          this.arcCol[o + k * 3] = _c.r * fade;
          this.arcCol[o + k * 3 + 1] = _c.g * fade;
          this.arcCol[o + k * 3 + 2] = _c.b * fade;
        }
        arcN++;
      }
    }
    this.arcGeo.setDrawRange(0, arcN * 2);
    this.arcGeo.attributes.position.needsUpdate = true;
    this.arcGeo.attributes.color.needsUpdate = true;
    this.arcs.visible = arcN > 0;

    for (const f of this.families.values()) {
      f.body.count = f.n;
      f.ink.count = f.n;
      // an all-packed family wrote nothing this frame: do not re-upload the buffers
      if (!f.dirty) continue;
      f.body.instanceMatrix.needsUpdate = true;
      f.aTint.needsUpdate = true; f.aEmi.needsUpdate = true; f.aPip.needsUpdate = true;
      f.aTag.needsUpdate = true; f.aPhase.needsUpdate = true; f.aWidth.needsUpdate = true;
    }
  }

  /**
   * Convenience: accepts contract `TokenState`s directly. The cabinet's origin is a
   * required argument — see `rebase`.
   */
  syncStates(tokens: TokenState[], origin: [number, number, number] | Vector3): void {
    this.sync(rebase(tokens, origin));
  }

  // ---------------------------------------------------------------- AURA

  private geistWisps(t: TokenView): void {
    if (!this.sparks) return;
    let trail = this.wisp.get(t.id);
    if (!trail) { trail = [new Vector3(), new Vector3(), new Vector3()]; this.wisp.set(t.id, trail); }
    const p = new Vector3(t.pos[0], t.pos[1], t.pos[2]);
    // three wisps lagging 80 / 160 / 240 ms
    trail[0].lerp(p, 0.35);
    trail[1].lerp(trail[0], 0.30);
    trail[2].lerp(trail[1], 0.26);
    for (let i = 0; i < 3; i++) {
      const f = 0.55 - i * 0.15;
      this.sparks.push(trail[i].x, trail[i].y, trail[i].z, t.radius * (1.7 - i * 0.35),
        _c.set(TAG_COLOR.Geist).multiplyScalar(f * 0.5).getHex());
    }
  }

  private fluchSmolder(t: TokenView): void {
    if (!this.smoke) return;
    if (Math.random() < 0.07) {
      this.smoke.spawn({
        pos: [t.pos[0] + (Math.random() - 0.5) * 0.4, t.pos[1] + 0.3, t.pos[2] + (Math.random() - 0.5) * 0.4],
        vel: [0, 0.9 + Math.random() * 0.6, 0],
        drag: 0.6, life: 1.1 + Math.random() * 0.6, size: 0.35, sizeEnd: 1.1,
        color: 0x6a2ea8, colorEnd: 0x1a0a2a, fade: 'inOut',
      });
    }
  }

  private feuerEmber(t: TokenView): void {
    if (!this.sparks) return;
    if (Math.random() < 0.25) {
      const a = Math.random() * Math.PI * 2;
      this.sparks.spawn({
        pos: [t.pos[0] + Math.cos(a) * t.radius, t.pos[1], t.pos[2] + Math.sin(a) * t.radius],
        vel: [0, 1.6 + Math.random(), 0], drag: 1.2, life: 0.5 + Math.random() * 0.4,
        size: 0.35, sizeEnd: 0.05, color: 0xffb23d, colorEnd: 0xc93a2e,
      });
    }
  }

  private kleberDrip(t: TokenView): void {
    if (!this.smoke) return;
    // a welded coin's drop has already run and set: it hangs, it does not swing
    const hang = t.stuck ? 0.72 : 0.5 + 0.25 * Math.sin(this.time * 2.2 + t.id);
    this.smoke.push(t.pos[0], t.pos[1] - t.radius * hang * 1.6, t.pos[2],
      t.radius * (t.stuck ? 1.1 : 0.9), t.stuck ? mixHex(TAG_COLOR.Kleber, 0x000000, 0.3) : TAG_COLOR.Kleber);
  }

  private echoImage(t: TokenView): void {
    if (!this.sparks) return;
    // half-alpha after-image lagging behind the coin
    this.sparks.push(t.pos[0], t.pos[1], t.pos[2], t.radius * 2.1,
      _c.set(TAG_COLOR.Echo).multiplyScalar(0.22).getHex());
  }

  update(dt: number, _camera: Camera): void {
    this.time += dt;
    for (const [id, s] of this.squash) {
      s.t -= dt * 3.4;
      if (s.t <= 0) this.squash.delete(id);
    }
  }

  dispose(): void {
    for (const f of this.families.values()) {
      f.body.geometry.dispose();
      (f.body.material as { dispose(): void }).dispose();
      (f.ink.material as { dispose(): void }).dispose();
    }
    this.arcGeo.dispose();
  }
}
